import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { EditedPassenger } from '@/components/organisms/edit-pax-details/edit-pax-details';
import { QueryId } from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseMutationUpdatePreBookingPassengersData =
  Booking.Responses.PatchPassengerResponse;

interface UseMutationUpdatePreBookingPassengersError {
  errors: any;
  message?: string;
}

interface UseMutationUpdatePreBookingPassengersParameters {
  brand?: Content.Properties.Brand;
  invalidationKeys?: QueryId[];
  parameters: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
  passengers: Omit<EditedPassenger, 'originalFields' | 'additionalFields'>[];
}

const useMutationUpdatePreBookingPassengers = () => {
  const locale = getLocale();
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    UseMutationUpdatePreBookingPassengersData,
    UseMutationUpdatePreBookingPassengersError,
    UseMutationUpdatePreBookingPassengersParameters
  > = useMutation(
    async parameters => {
      const fetchResponse = await fetch(
        '/api/booking/pre-booking/passengers/update-passengers',
        {
          body: JSON.stringify({
            bookingStateId: parameters.parameters.bookingStateId,
            brand: parameters.brand,
            passengers: parameters.passengers,
          }),
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'POST',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'bookingFlow.useMutationUpdatePreBookingPassengers',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      onError: async error => error,
      onSuccess: (_data, parameters) => {
        parameters.invalidationKeys?.forEach(queryId => {
          queryClient.invalidateQueries([queryId]);
        });
      },
    },
  );

  return mutation;
};

export default useMutationUpdatePreBookingPassengers;
